import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { EventsService } from '../../services/events.service';
import { Event } from '../../interfaces/event';
import { ReservationsService } from '../../services/reservations.service';
import { AuthService } from '../../services/auth.service';
import { MapComponent } from "../map/map.component";
import { MatDialog } from '@angular/material/dialog';
import { ResponseModalComponent } from '../response-modal/response-modal.component';
import { Title } from '@angular/platform-browser';
import { faCalendarAlt, faClock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ButtonComponent } from '../button/button.component';
import { BreweryLogoComponent } from '../brewery-logo/brewery-logo.component';
import { SkeletonComponent } from '../skeleton/skeleton.component';
import { TrackingService } from '../../services/tracking.service';
import { CartService } from '../../services/cart.service';
import { CartToastService } from '../../services/cart-toast.service';
import { TranslateModule } from '@ngx-translate/core';
import { BranchScheduleComponent } from '../branch-schedule/branch-schedule.component';
import { EventCardComponent } from '../event-card/event-card.component';

@Component({
  selector: 'app-event',
  standalone: true,
  imports: [CommonModule, RouterModule, MapComponent, FontAwesomeModule, ButtonComponent, BreweryLogoComponent, SkeletonComponent, TranslateModule, BranchScheduleComponent, EventCardComponent],
  templateUrl: './event.component.html',
  styleUrls: ['./event.component.scss']
})
export class EventComponent {
  event: Event | null = null;
  otherEvents: Event[] = [];
  isAuthenticated = false;
  loading: boolean = false;
  isLoadingEvent: boolean = true;
  hasPassed: boolean = true;
  faCalendarAlt = faCalendarAlt;
  faClock = faClock;
  expandedScheduleBranchId: number | null = null;
  swiperIndex = 0;
  activeOtherMapId: number | null = null;
  
  constructor(
    private route: ActivatedRoute,
    private eventsService: EventsService,
    private reservationService: ReservationsService,
    private authService: AuthService,
    private router: Router,
    private dialog: MatDialog,
    private title: Title,
    private trackingService: TrackingService,
    private cartService: CartService,
    private cartToastService: CartToastService
  ) { }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      const slug = params['slug'];
      this.loadEvent(slug);
    });
    this.authService.getTokenChanges().subscribe(token => {
      this.isAuthenticated = !!token;
    });
  }

  private isEventRestricted(eventId: number): boolean {
    const HIDDEN_EVENT_IDS_PROD = [4];
    if (!HIDDEN_EVENT_IDS_PROD.includes(eventId)) return false;
    const user = this.authService.getUser();
    return user?.roleId !== 2;
  }

  loadEvent(slug: string): void {
    this.isLoadingEvent = true;
    this.eventsService.getEvent(slug).subscribe(
      eventData => {
        if(!eventData) this.router.navigate([''])
        if (this.isEventRestricted(eventData.id)) {
          this.router.navigate(['']);
          return;
        }
        this.title.setTitle('Drink Hopping - Evento: ' + eventData.title)
        this.event = eventData
        this.hasPassed = eventData.hasPassed
        this.isLoadingEvent = false;

        this.trackingService.track('ViewContent', {
          content_name: eventData.title,
          content_ids: [eventData.id],
          content_type: 'product',
          value: eventData.price,
          currency: 'USD',
          content_category: eventData.eventType?.name
        });

        this.loadOtherEvents(eventData.id);
      },
      error => {
        console.error('Error loading event:', error);
        this.isLoadingEvent = false;
      }
    );
  }

  loadOtherEvents(currentEventId: number): void {
    this.eventsService.getEvents().subscribe(
      events => {
        this.otherEvents = events.filter(e => e.id !== currentEventId);
      },
      () => {}
    );
  }

  goToBrewery(slug: string): void {
    // Dependiendo de tu implementación podrías usar Router o Window.location
    // Ejemplo con Router:
    this.router.navigate(['/brewery', slug]);
    
    // O con window.location:
    // window.location.href = `/breweries/${slug}`;
  }

  reserveEvent(): void {
    if(this.loading){
      return
    }

    if(!this.isAuthenticated){
      this.openErrorModal('Debes estar logueado para reservar este evento', 'error','Un ultimo paso')
      return
    }

    if(this.hasPassed){
      const response = {error:{message:'El evento es pasado, no puedes reservar'}}
      this.openErrorModal('No puedes reservas un evento pasado. ¡Te invitamos a que veas el resto de opciones que brinda DrinkHopping!', 'error', 'Evento finalizado')
      return
    }

    this.loading = true;
    if (this.event) {
      this.reservationService.reserveEvent(this.event.id).subscribe(
        (res) => {
          this.loading = false
          this.router.navigate(['checkout', res.locator])
        },
        error => {
          console.error('Error reserving event:', error)
          this.openErrorModal(error.error.message, 'error', 'Algo ha salido mal...')
          this.loading = false
        },
      );
    }
  }

  openErrorModal(message: string, type:string, title:string) {   
    this.dialog.open(ResponseModalComponent, {
      data: {
        title: title,
        type: type,
        message: message ?? 'Intentalo mas tarde',
      }
    });
  }

  addToCart(event?: MouseEvent): void {

    if (this.hasPassed) {
      this.openErrorModal('No puedes agregar un evento pasado al carrito', 'error', 'Evento finalizado');
      return;
    }

    if (this.event) {
      // Por ahora agregamos con cantidad 1
      const quantity = 1;
      this.cartService.addEvent(this.event, quantity);
      

      // Calcular el precio final (precio original - descuento si existe)
      const originalPrice = Number(this.event.price ?? 0);
      const discountAmount = Number(this.event.discountAmount ?? 0);
      const finalPrice = originalPrice - discountAmount;

      this.trackingService.track('AddToCart', {
        content_ids: [this.event.id],
        content_name: this.event.title,
        content_type: 'product',
        value: finalPrice,
        currency: 'USD',
        content_category: this.event.eventType?.name ?? 'Evento'
      });

      this.cartToastService.show({
        title: this.event.title,
        subtitle: `$${finalPrice.toFixed(2)} · 1 persona`
      });
    }
  }

  toggleSchedule(branchId: number): void {
    this.expandedScheduleBranchId = this.expandedScheduleBranchId === branchId ? null : branchId;
  }

  branchHasSchedules(branch: any): boolean {
    return branch && branch.schedules && branch.schedules.length > 0;
  }

  eventHasAnySchedules(): boolean {
    if (!this.event?.eventBranches || this.event.eventBranches.length === 0) return false;
    return this.event.eventBranches.some(eb => this.branchHasSchedules(eb.branch));
  }

  // ── Swiper ────────────────────────────────────────────

  visibleCount = 3;

  get totalPages(): number {
    return Math.max(this.otherEvents.length - this.visibleCount + 1, 1);
  }

  getVisibleEvents(): Event[] {
    this.updateVisibleCount();
    const start = this.swiperIndex;
    return this.otherEvents.slice(start, start + this.visibleCount);
  }

  private updateVisibleCount(): void {
    if (typeof window === 'undefined') return;
    const width = window.innerWidth;
    if (width < 640) {
      this.visibleCount = 1;
    } else if (width < 1024) {
      this.visibleCount = 2;
    } else {
      this.visibleCount = 3;
    }
  }

  swiperNext(): void {
    this.updateVisibleCount();
    const max = this.otherEvents.length - this.visibleCount;
    this.swiperIndex = this.swiperIndex >= max ? 0 : this.swiperIndex + 1;
  }

  swiperPrev(): void {
    this.updateVisibleCount();
    const max = this.otherEvents.length - this.visibleCount;
    this.swiperIndex = this.swiperIndex <= 0 ? max : this.swiperIndex - 1;
  }
}
